// Typed fetch layer for the Help Center. Mirrors the API contract served by
// backend/routers/help.py (read-only, publish-gated; empty arrays in
// production until content is approved — every view here must degrade
// gracefully to a "nothing yet" state instead of crashing).

export interface HelpCategory {
    slug: string;
    title: string;
    short: string;
    icon: string;
    related: string[];
    articleCount: number;
}

export interface HelpArticleMeta {
    slug: string;
    category: string;
    categoryTitle: string;
    title: string;
    intent?: string | null;
    targetKeyword?: string | null;
    metaDescription?: string | null;
    summary?: string | null;
    lastReviewed?: string | null;
    url: string;
}

export interface HelpTocEntry {
    id: string;
    text: string;
    level: number;
}

export interface HelpArticleFull extends HelpArticleMeta {
    content: string;
    toc: HelpTocEntry[];
    structuredData: Record<string, unknown>[];
}

export interface HelpSearchHit {
    slug: string;
    category: string;
    categoryTitle: string;
    title: string;
    summary?: string | null;
    url: string;
    score: number;
}

const HELP_API_BASE = '/api/help';

/** Narrow an unknown JSON payload to an array, logging + defaulting to [] otherwise. */
const asArray = <T>(data: unknown, context: string): T[] => {
    if (Array.isArray(data)) return data as T[];
    console.error(`Expected array from ${context}, got:`, data);
    return [];
};

export async function fetchHelpCategories(lang = 'en'): Promise<HelpCategory[]> {
    try {
        const response = await fetch(`${HELP_API_BASE}/categories?lang=${encodeURIComponent(lang)}`);
        if (!response.ok) return [];
        const data: unknown = await response.json();
        return asArray<HelpCategory>(data, `${HELP_API_BASE}/categories`);
    } catch (error) {
        console.error('Error fetching help categories:', error);
        return [];
    }
}

export async function fetchHelpArticles(category: string | undefined, lang = 'en'): Promise<HelpArticleMeta[]> {
    try {
        const params = new URLSearchParams({ lang });
        if (category) params.set('category', category);
        const response = await fetch(`${HELP_API_BASE}/articles?${params.toString()}`);
        if (!response.ok) return [];
        const data: unknown = await response.json();
        return asArray<HelpArticleMeta>(data, `${HELP_API_BASE}/articles`);
    } catch (error) {
        console.error('Error fetching help articles:', error);
        return [];
    }
}

export async function fetchHelpArticle(
    category: string,
    slug: string,
    lang = 'en'
): Promise<HelpArticleFull | null> {
    try {
        const response = await fetch(
            `${HELP_API_BASE}/articles/${encodeURIComponent(category)}/${encodeURIComponent(slug)}?lang=${encodeURIComponent(lang)}`
        );
        if (!response.ok) return null;
        const data: unknown = await response.json();
        return (data ?? null) as HelpArticleFull | null;
    } catch (error) {
        console.error('Error fetching help article:', error);
        return null;
    }
}

export async function fetchHelpSearch(query: string, lang = 'en', limit = 20): Promise<HelpSearchHit[]> {
    const term = query.trim();
    if (term.length < 2) return [];
    try {
        const params = new URLSearchParams({ q: term, lang, limit: String(limit) });
        const response = await fetch(`${HELP_API_BASE}/search?${params.toString()}`);
        if (!response.ok) return [];
        const data: unknown = await response.json();
        return asArray<HelpSearchHit>(data, `${HELP_API_BASE}/search`);
    } catch (error) {
        console.error('Error fetching help search results:', error);
        return [];
    }
}
